import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import find from 'lodash/find';
import { useHistory } from 'react-router-dom';

import { Cell, Grid } from 'formlabs-web-components/Grid';
import { Header } from 'formlabs-web-components/Type';
import { Button } from 'formlabs-web-components/Link';

import ProductOptionCard from './ProductOptionCard';
import OptionSection from './OptionSection';
import OptionNavigation from './OptionNavigation';
import useIsMobile from '../../../hooks/useIsMobile';
import CountryContext from '../../../context/country';
import { analytics } from '../../../utils/index';
import { convert, participate } from '../../experiment';
import useBusinessHours from '../../../hooks/useBusinessHours';

import '../ProductDetailPlugin.module.css';

const OptionSelector = ({
  bundleOptions,
  toggleSelector,
  selector,
  price,
  handleOptionChange,
  amount,
  sku,
  name,
  bundle,
  step,
  stepData,
  productName,
  toggleCallUs
}) => {
  const [t] = useTranslation();
  const mobile = useIsMobile(640);
  const { phoneNumberSales, path } = useContext(CountryContext);
  const isBusinessHours = useBusinessHours();
  const history = useHistory();
  const [isForm3ServiceAddonVariant, setIsForm3ServiceAddonVariant] = useState(
    false
  );

  const displayEmailAddress = isForm3ServiceAddonVariant
    ? 'sales+form3@formlabs.com'
    : 'sales@formlabs.com';
  const displayPhoneNumber = isForm3ServiceAddonVariant
    ? ' +1 617 586 8607'
    : phoneNumberSales;
  const displaySKU = isForm3ServiceAddonVariant ? 'PKG-F3-NO-SERVICE' : sku;

  const browserBack = () => {
    if (step === 0 && selector) {
      toggleSelector();
    }
  };

  const back = () => {
    history.goBack();
    browserBack();
  };

  const navigateSteps = i => {
    if (i < 0) {
      back();
    } else history.push(`/step${i}`);
  };

  useEffect(
    () => {
      return () => {
        if (history.action === 'POP') {
          browserBack();
        }

        document.body.scrollTop = 0; // For Safari
        document.documentElement.scrollTop = 0;
      };
    },
    [step]
  );

  const convertPrinter = () => {
    convert('2020_08_US_Form3_Required_Service');
  };
  useEffect(
    () => {
      switch (step) {
        case -1:
          toggleSelector();
          break;
        default:
          const { stepName, pageName } = stepData[step].analytics;
          analytics.page(pageName, {
            path: `${window.location.pathname}#${history.location.pathname}`,
            step_position: step,
            step_name: stepName
          });
          break;
      }
    },
    [step]
  );
  const phoneNumberTracking = () => {
    analytics.track('call_to_order');
    convertPrinter();
  };

  const steps = stepData
    .filter(step => {
      return step.progressTitle;
    })
    .map(step => step.progressTitle);
  return (
    <>
      <div>
        <Grid direction="x" hasContainer={true} primaryAlignment="align-center">
          {mobile && (
            <Cell mediumSize={20}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingBottom: '1rem'
                }}
              >
                <Button onClick={back} color="borderless-blue" arrow="left">
                  {t('Back')}
                </Button>
                {step !== steps.length && steps.length > 1 && (
                  <div>
                    {t('Step')} {step + 1}/{steps.length}
                  </div>
                )}
              </div>
            </Cell>
          )}
          <Cell mediumSize={20}>
            <div styleName="center">
              <Header level={2} displayAs={3} weight={700}>
                {stepData[step].title}
              </Header>
            </div>
            {stepData.map(stepDataSingle => {
              if (stepDataSingle.type === 'option') {
                return (
                  <div
                    styleName={
                      step === stepDataSingle.order ? '' : 'hidden-step'
                    }
                    key={stepDataSingle.title}
                  >
                    {stepData[stepDataSingle.order].sections.map(section => {
                      if (!section.options.length) {
                        return null;
                      }

                      return (
                        <OptionSection
                          title={section.title}
                          key={section.title}
                          subtitle={section.subtitle}
                          handleOptionChange={handleOptionChange}
                        >
                          <Grid
                            direction="x"
                            primaryAlignment="align-center"
                            gutters="margin"
                          >
                            {section.copy}
                            <Cell mediumSize="8" smallOrder={1} mediumOrder={2}>
                              <ProductOptionCard
                                handleOptionChange={handleOptionChange}
                                required={section.required}
                                option={
                                  section.options[0].transformFn
                                    ? section.options[0].transformFn(bundle)
                                    : section.options[0].id
                                    ? find(bundleOptions, {
                                        option_id: section.options[0].id
                                      })
                                    : section.options[0].fullOption
                                }
                                optionData={section.options[0]}
                                images={section.options[0].images}
                                recommended={section.options[0].recommended}
                                combine={section.combineOptions}
                                type={section.options[0].type}
                                step={stepDataSingle.order}
                                currentStep={step}
                                amount={amount}
                                title={section.options[0].productName}
                                bundle={bundle}
                              />
                            </Cell>
                          </Grid>
                        </OptionSection>
                      );
                    })}
                  </div>
                );
              }
              return (
                <div
                  styleName={step === stepDataSingle.order ? '' : 'hidden-step'}
                >
                  <Header level={3} displayAs={4} weight={700}>
                    {t('Contact us to complete your order')}
                  </Header>
                  <p>
                    {t(
                      'Your chosen configuration is only available via our sales team, please contact a sales representative to process your order.'
                    )}
                  </p>
                  <hr styleName="light-hr" />
                  <div styleName="call-to-order">
                    <Header level={3} displayAs={5} weight={700}>
                      {productName}
                    </Header>
                    {bundle.get(7001028) && bundle.get(7001028).size > 1 && (
                      <p>{t('With Form Wash and Form Cure')}</p>
                    )}
                    <p>
                      <strong>{t('Product Reference Number')}:</strong>
                    </p>
                    <p styleName="sku-display grey-text">{displaySKU}</p>
                    <p>
                      {isBusinessHours ? (
                        <Button
                          color="dark"
                          onClick={phoneNumberTracking}
                          to={`tel:${displayPhoneNumber}`}
                        >
                          {t('Call {{phoneNumber}}', {
                            phoneNumber: displayPhoneNumber
                          })}
                        </Button>
                      ) : (
                        <Button
                          color="dark"
                          onClick={phoneNumberTracking}
                          to={`mailto:${displayEmailAddress}`}
                        >
                          {t('Email {{email}}', {
                            email: displayEmailAddress
                          })}
                        </Button>
                      )}
                    </p>

                    <p styleName="grey-text no-margin-bottom">
                      {t('Call {{phoneNumber}}', {
                        phoneNumber: displayPhoneNumber
                      })}
                      <br />
                      {t('Email {{email}}', {
                        email: displayEmailAddress
                      })}
                    </p>
                    <p styleName="grey-text no-margin-top">
                      {path === '/'
                        ? t('Monday – Friday: 9am to 6pm EST')
                        : t('Monday – Friday: 9am to 6pm CET')}
                    </p>
                  </div>
                </div>
              );
            })}
          </Cell>
        </Grid>
        <OptionNavigation
          step={step}
          mobile={mobile}
          steps={steps}
          back={navigateSteps}
          price={price}
          amount={amount}
          sku={sku}
          name={name}
          bundle={bundle}
          path={path}
          phoneNumberTracking={phoneNumberTracking}
          phoneNumberSales={displayPhoneNumber}
          emailAddressSales={displayEmailAddress}
          convertPrinter={convertPrinter}
          isBusinessHours={isBusinessHours}
          toggleCallUs={toggleCallUs}
        />
      </div>
    </>
  );
};
export default OptionSelector;
